(() => {
  const SELECTORS = {
    cartCount: "[data-ion-cart-count]",
    cartOpen: "[data-ion-cart-open]",
  };

  const moneyFallback = (cents) => {
    const value = Number(cents || 0) / 100;
    return new Intl.NumberFormat(document.documentElement.lang || "en-US", {
      style: "currency",
      currency: window.Shopify?.currency?.active || "USD",
    }).format(value);
  };

  const refreshCartCount = async () => {
    try {
      const response = await fetch(`${window.routes?.cart_url || "/cart"}.js`, {
        headers: { Accept: "application/json" },
      });
      if (!response.ok) return;
      const cart = await response.json();
      document.querySelectorAll(SELECTORS.cartCount).forEach((element) => {
        element.textContent =
          cart.item_count > 0 ? String(cart.item_count) : "";
        element.dataset.count = String(cart.item_count || 0);
      });
    } catch (error) {
      console.warn("[ION merch] Cart count could not be refreshed.", error);
    }
  };

  class IonCountdown extends HTMLElement {
    connectedCallback() {
      this.output = this.querySelector("[data-ion-countdown-value]");
      this.hero = this.closest(".ion-hero");
      this.target = new Date(this.dataset.target || "");

      if (!this.output || Number.isNaN(this.target.getTime())) {
        this.hidden = true;
        return;
      }

      this.tick();
      this.timer = window.setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
    }

    tick() {
      const remaining = this.target.getTime() - Date.now();
      if (remaining <= 0) {
        window.clearInterval(this.timer);
        this.output.textContent = this.dataset.expiredText || "DROP CLOSED";
        this.hero?.setAttribute("data-expired", "true");
        return;
      }

      const days = Math.floor(remaining / 86400000);
      const hours = Math.floor((remaining % 86400000) / 3600000);
      const minutes = Math.floor((remaining % 3600000) / 60000);
      const seconds = Math.floor((remaining % 60000) / 1000);
      this.output.textContent = `${days}D ${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
    }
  }

  class IonProductCard extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      const dataElement = this.querySelector("[data-ion-variants]");
      try {
        this.variants = JSON.parse(dataElement?.textContent || "[]");
      } catch (error) {
        console.error("[ION merch] Variant data is invalid.", error);
        this.variants = [];
      }

      this.colorIndex = Number(this.dataset.colorIndex ?? -1);
      this.sizeIndex = Number(this.dataset.sizeIndex ?? -1);
      this.scarcityThreshold = Number(this.dataset.scarcityThreshold || 50);
      this.isLimited = this.dataset.isLimited === "true";
      this.editionLimit = Number(this.dataset.editionLimit || 0);
      this.unitsSold = Number(this.dataset.unitsSold || 0);
      this.editorialBadge = this.dataset.editorialBadge || "";
      this.defaultButtonLabel = this.dataset.buttonLabel || "GET THIS ONE";

      this.image = this.querySelector("[data-ion-product-image]");
      this.hoverImage = this.querySelector("[data-ion-product-hover-image]");
      this.media = this.querySelector(".ion-product-card__media");
      this.fallbackHoverImage = this.hoverImage?.getAttribute("src") || "";
      this.price = this.querySelector("[data-ion-product-price]");
      this.badge = this.querySelector("[data-ion-inventory-badge]");
      this.colorName = this.querySelector("[data-ion-color-name]");
      this.cta = this.querySelector("[data-ion-product-cta]");
      this.form = this.querySelector("form");
      this.variantInput = this.form?.querySelector('[name="id"]');
      this.dialog = this.querySelector("[data-ion-size-dialog]");
      this.sizeOptions = this.querySelector(".ion-size-options");
      this.feedback = this.querySelector("[data-ion-add-feedback]");
      this.feedbackText = this.querySelector("[data-ion-add-feedback-text]");
      this.feedbackIcon = this.querySelector("[data-ion-add-feedback-icon]");
      this.feedbackLink = this.querySelector("[data-ion-add-feedback-link]");
      this.adding = false;
      this.pendingVariantId = null;
      this.pendingSize = null;
      this.addMessage = this.querySelector(".add-message");

      const initialVariantId = String(this.variantInput?.value || "");
      this.currentVariant =
        this.variants.find(
          (variant) => String(variant.id) === initialVariantId,
        ) || this.variants[0];
      this.selectedColor =
        this.colorIndex >= 0
          ? this.currentVariant?.options?.[this.colorIndex]
          : null;

      this.querySelectorAll("[data-ion-color-value]").forEach((button) => {
        button.addEventListener("click", () =>
          this.selectColor(button.dataset.ionColorValue),
        );
      });

      this.cta?.addEventListener("click", () => this.handleCta());
      this.querySelector("[data-ion-dialog-close]")?.addEventListener(
        "click",
        () => this.dialog?.close(),
      );

      this.querySelectorAll("[data-ion-size-value]").forEach((button) => {
        button.addEventListener("click", () =>
          this.selectSize(button.dataset.ionSizeValue),
        );
      });

      this.onStandardCartError = (event) => {
        if (this.pendingVariantId === null) return;
        this.handleCartError({ errors: event.error });
      };
      this.addEventListener("shopify:cart:error", this.onStandardCartError);

      this.bindCartFeedback();
      this.syncCard();
    }

    disconnectedCallback() {
      if (typeof this.unsubscribeCartUpdate === "function")
        this.unsubscribeCartUpdate();
      if (typeof this.unsubscribeCartError === "function")
        this.unsubscribeCartError();
      this.removeEventListener("shopify:cart:error", this.onStandardCartError);
      window.clearTimeout(this.feedbackTimer);
      window.clearTimeout(this.ctaTimer);
      window.clearTimeout(this.requestTimer);
    }

    bindCartFeedback() {
      if (
        typeof subscribe !== "function" ||
        typeof PUB_SUB_EVENTS === "undefined"
      )
        return;

      this.unsubscribeCartUpdate = subscribe(
        PUB_SUB_EVENTS.cartUpdate,
        (event) => {
          if (!this.isPendingCartEvent(event)) return;
          this.handleCartSuccess();
        },
      );

      this.unsubscribeCartError = subscribe(
        PUB_SUB_EVENTS.cartError,
        (event) => {
          if (!this.isPendingCartEvent(event)) return;
          this.handleCartError(event);
        },
      );
    }

    isPendingCartEvent(event) {
      return (
        this.pendingVariantId !== null &&
        event?.source === "product-form" &&
        String(event.productVariantId) === String(this.pendingVariantId)
      );
    }

    showFeedback(state, message) {
      if (!this.feedback || !this.feedbackText) return;

      window.clearTimeout(this.feedbackTimer);
      this.feedback.dataset.state = state;
      this.feedbackText.textContent = message;
      this.feedback.hidden = false;

      if (this.feedbackIcon) {
        this.feedbackIcon.textContent =
          state === "success" ? "✓" : state === "error" ? "!" : "";
      }

      if (this.feedbackLink) this.feedbackLink.hidden = state !== "success";

      if (state !== "loading") {
        this.feedbackTimer = window.setTimeout(
          () => {
            this.feedback.hidden = true;
          },
          state === "success" ? 4500 : 6000,
        );
      }
    }

    handleCartSuccess() {
      const size = this.pendingSize;
      window.clearTimeout(this.requestTimer);
      this.adding = false;
      this.pendingVariantId = null;
      this.pendingSize = null;
      this.sizeOptions?.removeAttribute("aria-busy");
      this.dialog?.close();
      this.updateSizeOptions();
      this.showFeedback(
        "success",
        size ? `Size ${size} added to your cart.` : "Added to your cart.",
      );

      if (this.cta) {
        window.clearTimeout(this.ctaTimer);
        this.cta.disabled = true;
        this.cta.style.color = "green";
        this.cta.textContent = "ADDED ✓";
        this.ctaTimer = window.setTimeout(() => this.updateCta(), 1500);
      }

      refreshCartCount();
    }

    handleCartError(event) {
      window.clearTimeout(this.requestTimer);
      this.adding = false;
      this.pendingVariantId = null;
      this.pendingSize = null;
      this.sizeOptions?.removeAttribute("aria-busy");
      this.updateCta();
      this.updateSizeOptions();

      const detail = typeof event?.errors === "string" ? event.errors : "";
      this.showFeedback(
        "error",
        detail || "That item could not be added. Please try again.",
      );
    }

    variantsForColor() {
      if (this.colorIndex < 0 || !this.selectedColor) return this.variants;
      return this.variants.filter(
        (variant) => variant.options?.[this.colorIndex] === this.selectedColor,
      );
    }

    firstAvailableVariant() {
      return (
        this.variantsForColor().find((variant) => variant.available) ||
        this.variantsForColor()[0]
      );
    }

    selectColor(color) {
      this.selectedColor = color;
      this.currentVariant = this.firstAvailableVariant();
      this.syncCard();
    }

    syncCard() {
      const candidate = this.currentVariant || this.firstAvailableVariant();

      this.querySelectorAll("[data-ion-color-value]").forEach((button) => {
        const selected = button.dataset.ionColorValue === this.selectedColor;
        button.setAttribute("aria-pressed", String(selected));
      });

      if (this.colorName && this.selectedColor)
        this.colorName.textContent = this.selectedColor;

      if (candidate?.image && this.image) {
        this.image.removeAttribute("srcset");
        this.image.src = candidate.image;
        if (candidate.imageAlt) this.image.alt = candidate.imageAlt;
      }

      this.updateHoverImage(candidate);

      if (this.price && candidate) {
        this.price.textContent =
          candidate.priceFormatted || moneyFallback(candidate.price);
      }

      this.updateInventoryBadge();
      this.updateCta();
      this.updateSizeOptions();
    }

    updateHoverImage(candidate) {
      if (!this.hoverImage || !this.media) return;

      const variants = this.variants || [];
      const currentImage = candidate?.image || this.image?.getAttribute("src") || "";
      const currentIndex = Math.max(
        0,
        variants.findIndex(
          (variant) => String(variant.id) === String(candidate?.id),
        ),
      );

      let nextImage = "";
      for (let offset = 1; offset <= variants.length; offset += 1) {
        const variant = variants[(currentIndex + offset) % variants.length];
        if (variant?.image && variant.image !== currentImage) {
          nextImage = variant.image;
          break;
        }
      }

      if (!nextImage && this.fallbackHoverImage !== currentImage) {
        nextImage = this.fallbackHoverImage;
      }

      if (!nextImage) {
        this.hoverImage.hidden = true;
        this.media.classList.remove("ion-product-card__media--has-hover");
        return;
      }

      this.hoverImage.removeAttribute("srcset");
      this.hoverImage.src = nextImage;
      this.hoverImage.hidden = false;
      this.media.classList.add("ion-product-card__media--has-hover");
    }

    updateInventoryBadge() {
      if (!this.badge) return;
      const variants = this.variantsForColor();
      const available = variants.filter((variant) => variant.available);
      if (available.length === 0) {
        this.badge.textContent = "SOLD OUT";
        this.badge.dataset.state = "sold-out";
        return;
      }

      this.badge.dataset.state = "available";

      /*
       * –––––––– Pick Up Here ––––––
       * Future limited-drop count:
       * Compare custom.units_sold against custom.limit. This intentionally does
       * not use Printful variant inventory quantities.
       *
       * if (this.isLimited && this.editionLimit > 0) {
       *   const remaining = Math.max(0, this.editionLimit - this.unitsSold);
       *   if (remaining > 0) {
       *     this.badge.textContent = `ONLY ${remaining} LEFT`;
       *     return;
       *   }
       * }
       */

      this.badge.textContent =
        this.editorialBadge || (this.isLimited ? "LIMITED RUN" : "AVAILABLE");
    }

    updateCta() {
      this.updateAddMessage();
      if (!this.cta) return;
      const hasAvailableVariant = this.variantsForColor().some(
        (variant) => variant.available,
      );
      this.cta.disabled = this.adding || !hasAvailableVariant || !this.form;
      this.cta.style.color = "green";

      this.cta.textContent = this.adding
        ? "ADDING…"
        : hasAvailableVariant
          ? this.defaultButtonLabel
          : "SOLD OUT";
      this.cta.setAttribute(
        "aria-haspopup",
        this.sizeIndex >= 0 ? "dialog" : "false",
      );
      this.cta.style.color = "black";
    }
    updateAddMessage() {
      if (!this.addMessage) return;

      window.clearInterval(this.addMessageTimer);

      if (!this.adding) {
        this.addMessage.textContent = "";
        this.addMessage.hidden = true;
        return;
      }

      this.addMessage.hidden = false;

      let dotCount = 0;
      this.addMessage.textContent = "ADDING.";

      this.addMessageTimer = window.setInterval(() => {
        dotCount = (dotCount % 3) + 1;
        this.addMessage.textContent = `ADDING.${".".repeat(dotCount)}`;
      }, 350);
    }
    updateSizeOptions() {
      if (this.sizeIndex < 0) return;
      this.querySelectorAll("[data-ion-size-value]").forEach((button) => {
        const value = button.dataset.ionSizeValue;
        const variant = this.variantsForColor().find(
          (item) => item.options?.[this.sizeIndex] === value,
        );
        button.disabled = this.adding || !variant?.available;
        button.setAttribute(
          "aria-pressed",
          String(variant?.id === this.currentVariant?.id),
        );
      });
    }

    handleCta() {
      if (!this.form) return;
      if (this.sizeIndex >= 0 && this.dialog) {
        this.updateSizeOptions();
        this.dialog.showModal();
        this.dialog
          .querySelector("[data-ion-size-value]:not([disabled])")
          ?.focus();
        return;
      }

      const variant = this.firstAvailableVariant();
      if (variant) this.submitVariant(variant);
    }

    selectSize(size) {
      const variant = this.variantsForColor().find(
        (item) => item.options?.[this.sizeIndex] === size && item.available,
      );
      if (!variant) return;
      this.currentVariant = variant;
      this.adding = true;
      this.pendingVariantId = variant.id;
      this.pendingSize = size;
      this.sizeOptions?.setAttribute("aria-busy", "true");
      this.syncCard();
      this.showFeedback("loading", `Adding size ${size}…`);
      this.submitVariant(variant);
      this.requestTimer = window.setTimeout(() => {
        if (this.pendingVariantId !== null) this.handleCartError({});
      }, 15000);
    }

    submitVariant(variant) {
      if (!this.form || !this.variantInput || !variant?.available) return;
      this.variantInput.value = variant.id;
      this.variantInput.dispatchEvent(new Event("change", { bubbles: true }));
      this.form.requestSubmit();
      window.setTimeout(refreshCartCount, 900);
      window.setTimeout(refreshCartCount, 1800);
    }
  }

  if (!customElements.get("ion-countdown"))
    customElements.define("ion-countdown", IonCountdown);
  if (!customElements.get("ion-product-card"))
    customElements.define("ion-product-card", IonProductCard);


  const initializeStickerFeedback = () => {
    document.querySelectorAll("[data-ion-sticker-card]").forEach((card) => {
      if (card.dataset.ionStickerBound === "true") return;

      const productForm = card.querySelector("product-form");
      const form = productForm?.querySelector("form");
      const variantInput = form?.querySelector('[name="id"]');
      const button = form?.querySelector('[type="submit"]');
      const buttonText = button?.querySelector("[data-ion-sticker-button-text]");
      const feedback = card.querySelector("[data-ion-add-feedback]");
      const feedbackText = card.querySelector("[data-ion-add-feedback-text]");
      const feedbackIcon = card.querySelector("[data-ion-add-feedback-icon]");
      const feedbackLink = card.querySelector("[data-ion-add-feedback-link]");

      if (
        !form ||
        !variantInput ||
        !button ||
        !buttonText ||
        !feedback ||
        !feedbackText ||
        typeof subscribe !== "function" ||
        typeof PUB_SUB_EVENTS === "undefined"
      )
        return;

      card.dataset.ionStickerBound = "true";
      const defaultButtonText = buttonText.textContent.trim();
      let pendingVariantId = null;
      let feedbackTimer;
      let buttonTimer;
      let requestTimer;

      const showFeedback = (state, message) => {
        window.clearTimeout(feedbackTimer);
        feedback.dataset.state = state;
        feedbackText.textContent = message;
        feedback.hidden = false;

        if (feedbackIcon) {
          feedbackIcon.textContent =
            state === "success" ? "✓" : state === "error" ? "!" : "";
        }

        if (feedbackLink) feedbackLink.hidden = state !== "success";

        if (state !== "loading") {
          feedbackTimer = window.setTimeout(
            () => {
              feedback.hidden = true;
            },
            state === "success" ? 4500 : 6000,
          );
        }
      };

      const resetButton = () => {
        window.clearTimeout(buttonTimer);
        buttonText.textContent = defaultButtonText;
      };

      form.addEventListener("submit", () => {
        if (button.disabled) return;

        pendingVariantId = String(variantInput.value || "");
        showFeedback("loading", "Adding stickers…");

        window.clearTimeout(requestTimer);
        requestTimer = window.setTimeout(() => {
          if (pendingVariantId === null) return;
          pendingVariantId = null;
          resetButton();
          showFeedback(
            "error",
            "The stickers could not be added. Please try again.",
          );
        }, 15000);
      });

      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (
          pendingVariantId === null ||
          event?.source !== "product-form" ||
          String(event.productVariantId) !== pendingVariantId
        )
          return;

        window.clearTimeout(requestTimer);
        pendingVariantId = null;
        showFeedback("success", "Stickers added to your cart.");
        buttonText.textContent = "ADDED ✓";
        buttonTimer = window.setTimeout(resetButton, 1500);
        refreshCartCount();
      });

      subscribe(PUB_SUB_EVENTS.cartError, (event) => {
        if (
          pendingVariantId === null ||
          event?.source !== "product-form" ||
          String(event.productVariantId) !== pendingVariantId
        )
          return;

        window.clearTimeout(requestTimer);
        pendingVariantId = null;
        resetButton();

        const detail = typeof event?.errors === "string" ? event.errors : "";
        showFeedback(
          "error",
          detail || "The stickers could not be added. Please try again.",
        );
      });
    });
  };

  const initializePage = () => {
    initializeStickerFeedback();
    if (!document.querySelector(".ion-merch-shell")) return;
    document.body.classList.add("ion-merch-page");

    document.querySelectorAll(SELECTORS.cartOpen).forEach((button) => {
      if (button.dataset.ionCartBound === "true") return;
      button.dataset.ionCartBound = "true";
      button.addEventListener("click", () => {
        const drawer = document.querySelector("cart-drawer");
        if (drawer?.open) drawer.open(button);
        else window.location.assign(window.routes?.cart_url || "/cart");
      });
    });

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      document
        .querySelectorAll(".ion-merch video[autoplay]")
        .forEach((video) => {
          video.pause();
          video.removeAttribute("autoplay");
        });
    }
  };

  initializePage();
  document.addEventListener("shopify:section:load", initializePage);
})();
